/**
 * <sa-delivery-estimate data-lead-days data-prefix>
 * Recomputes the "Receive by <date>" line client-side from the current date, so
 * the estimate can never be frozen by Shopify's full-page cache. Falls back to
 * the server-rendered text if the lead-day count is missing or invalid.
 */
class DeliveryEstimate extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  // Recompute from the current date. Idempotent and safe to call repeatedly —
  // invoked on connect and whenever the shopper returns to the page (see below).
  render() {
    const days = parseInt(this.dataset.leadDays, 10);
    if (!Number.isFinite(days)) return; // keep the server-rendered fallback

    const date = new Date();
    date.setDate(date.getDate() + days); // calendar-day math, DST-safe

    const lang = document.documentElement.lang || 'en';
    const weekday = this.#part(lang, date, { weekday: 'short' });
    const month = this.#part(lang, date, { month: 'short' });
    const day = date.getDate();
    const prefix = (this.dataset.prefix || '').trim();

    this.textContent = `${prefix} ${weekday} ${day}${this.#ordinal(day)} ${month}`.trim();
  }

  #part(lang, date, options) {
    try {
      return new Intl.DateTimeFormat(lang, options).format(date);
    } catch (error) {
      return new Intl.DateTimeFormat('en', options).format(date);
    }
  }

  #ordinal(n) {
    // 11th, 12th, 13th are special-cased; all other teens fall through to the
    // last-digit rule below.
    const v = n % 100;
    if (v >= 11 && v <= 13) return 'th';
    switch (n % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }
}

if (!customElements.get('sa-delivery-estimate')) {
  customElements.define('sa-delivery-estimate', DeliveryEstimate);
}

// A page can outlive the day it was rendered on: a tab left open for days, or a
// back/forward (bfcache) restore, would otherwise keep a date computed earlier.
// Recompute every instance whenever the shopper returns to the page.
const refreshDeliveryEstimates = () => {
  document.querySelectorAll('sa-delivery-estimate').forEach((el) => {
    if (typeof el.render === 'function') el.render();
  });
};

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') refreshDeliveryEstimates();
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshDeliveryEstimates();
});
